import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { Leaf, X, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';

/**
 * Ryan is licensed, so he is the one who can look at a lawn and say what it
 * needs. He picks the problem he is standing in front of, the customer gets the
 * offer, and if they say yes and the work gets done he keeps 10% of it.
 *
 * The commission is only real once the treatment is finished — offering things
 * pays nobody.
 */
type Item = { key: string; issue: string; treatment: string; detail: string; typical_price: number };

export default function SuggestTreatment({ jobId, onClose }: { jobId: number; onClose: () => void }) {
  const [issue, setIssue] = useState<string | null>(null);
  const [picked, setPicked] = useState<Item | null>(null);
  const [price, setPrice] = useState('');
  const [detail, setDetail] = useState('');

  const { data } = useQuery(['treatment-catalogue'], () =>
    api.get('/treatments/catalogue').then(r => r.data as { grouped: Record<string, Item[]>; commission_pct: number }));

  const pct = data?.commission_pct ?? 10;
  const amount = Number(price) || picked?.typical_price || 0;
  const yours = Math.round(amount * (pct / 100) * 100) / 100;

  const send = useMutation(
    () => api.post('/treatments', {
      job_id: jobId,
      issue: picked?.issue,
      treatment_key: picked?.key,
      treatment: picked?.treatment,
      detail: detail.trim() || picked?.detail,
      price: amount,
    }),
    {
      onSuccess: (r: any) => { toast.success(r.data.message); onClose(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not send that'); },
    });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-t-2xl w-full max-w-lg p-5 space-y-4 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900 flex items-center gap-2">
            <Leaf className="h-5 w-5 text-emerald-600" /> Suggest a treatment
          </h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        {!issue && (
          <>
            <p className="text-sm text-gray-600">What's wrong with this yard?</p>
            <div className="space-y-2">
              {Object.keys(data?.grouped || {}).map(g => (
                <button key={g} onClick={() => setIssue(g)}
                  className="w-full flex items-center justify-between text-left px-4 py-3 rounded-xl border border-gray-200 active:bg-gray-50">
                  <span className="text-sm font-medium text-gray-900">{g}</span>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                </button>
              ))}
            </div>
          </>
        )}

        {issue && !picked && (
          <>
            <button onClick={() => setIssue(null)} className="text-sm text-brand-600 font-medium">← {issue}</button>
            <div className="space-y-2">
              {(data?.grouped?.[issue] || []).map(t => (
                <button key={t.key} onClick={() => { setPicked(t); setPrice(String(t.typical_price)); }}
                  className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 active:bg-gray-50">
                  <p className="text-sm font-semibold text-gray-900">{t.treatment}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t.detail}</p>
                  <p className="text-xs text-emerald-700 font-medium mt-1">
                    usually ${t.typical_price} · you'd make ${Math.round(t.typical_price * (pct / 100) * 100) / 100}
                  </p>
                </button>
              ))}
            </div>
          </>
        )}

        {picked && (
          <>
            <button onClick={() => setPicked(null)} className="text-sm text-brand-600 font-medium">← {picked.issue}</button>
            <div>
              <p className="text-sm font-semibold text-gray-900">{picked.treatment}</p>
              <p className="text-xs text-gray-500">{picked.detail}</p>
            </div>
            <label className="block">
              <span className="text-xs font-medium text-gray-600">Price</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-gray-500">$</span>
                <input type="number" min={0} step={5} value={price} onChange={e => setPrice(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-base" />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600">What you saw (the customer reads this)</span>
              <textarea value={detail} onChange={e => setDetail(e.target.value)} rows={3}
                placeholder={picked.detail}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </label>
            <p className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg px-3 py-2">
              You make <b>${yours.toFixed(2)}</b> ({pct}% of ${amount.toFixed(2)}) — paid when the treatment is done, not now.
            </p>
            <button onClick={() => send.mutate()} disabled={send.isLoading || !(amount > 0)}
              className="w-full py-3 rounded-xl bg-emerald-600 text-white font-semibold disabled:opacity-50">
              {send.isLoading ? 'Sending…' : 'Send it to the customer'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
